import { cx } from "@emotion/css";
import { FileMinus, Trash2 } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	PrimaryButton,
	TextPrimaryButton,
} from "src/components/Default/LekUI/Button";
import { Checkbox } from "src/components/Default/LekUI/Checkbox";
import {
	Modal,
	ModalFooter,
	ModalHeader,
} from "src/components/Modals/Components/ModalsComponents";
import { useFontSize } from "src/hooks/useFontSize";
import { ModalMaxWidth } from "src/types/enums";

export interface DeleteItemModalProps {
	onClose: () => void;
	/** Akcja usuwania. Modal pokazuje loading i zamyka się po sukcesie. */
	onDelete: () => Promise<unknown>;
	title: string;
	description: ReactNode;
	confirmText: string;
	/** Co usuwamy (nazwa elementu albo np. "3 egzaminy") — box pod opisem. */
	itemLabel?: ReactNode;
	/** Dodatkowa treść pod opisem (np. lista konsekwencji). */
	children?: ReactNode;
	/** Gdy podany — checkbox, który trzeba zaznaczyć, żeby odblokować usuwanie. */
	confirmCheckboxLabel?: string;
}

export const DeleteItemModal = ({
	onClose,
	onDelete,
	title,
	description,
	confirmText,
	itemLabel,
	children,
	confirmCheckboxLabel,
}: DeleteItemModalProps) => {
	const { t } = useTranslation();
	const { modalTextFontSizeClass } = useFontSize();
	const [isLoading, setIsLoading] = useState(false);
	const [isConfirmed, setIsConfirmed] = useState(!confirmCheckboxLabel);

	const remove = async () => {
		setIsLoading(true);
		await onDelete().finally(() => setIsLoading(false));
		onClose();
	};

	return (
		<Modal onClose={onClose} maxWidth={ModalMaxWidth.sm}>
			<div className="delete-item-modal" data-test-id="delete-item-modal">
				<ModalHeader
					Icon={Trash2}
					iconClassName="icon-error"
					title={title}
					description={description}
					hasCloseButton
				/>
				{itemLabel && (
					<div className="delete-item-modal--danger-box delete-item-modal--summary">
						<span className="delete-item-modal--summary-icon">
							<FileMinus className="icon-small icon-error" />
						</span>
						<span className={cx(modalTextFontSizeClass, "text-dark-grey")}>
							{t("modals.deleteItem.toDelete", "Do usunięcia:")}{" "}
							<strong className="text-black">{itemLabel}</strong>
						</span>
					</div>
				)}
				{children}
				{confirmCheckboxLabel && (
					<label className="delete-item-modal--confirm">
						<Checkbox
							checked={isConfirmed}
							onChange={(e) => setIsConfirmed(e.target.checked)}
							slotProps={{
								input: {
									"data-test-id": "delete-confirm-checkbox",
								} as React.InputHTMLAttributes<HTMLInputElement>,
							}}
						/>
						<span className={modalTextFontSizeClass}>
							{confirmCheckboxLabel}
						</span>
					</label>
				)}
				<ModalFooter className="delete-item-modal--footer">
					<TextPrimaryButton
						className="btn-size-big border-radius-12"
						onClick={onClose}
						data-test-id="delete-cancel-button"
					>
						{t("commands.cancel")}
					</TextPrimaryButton>
					<PrimaryButton
						className="btn-size-big border-radius-12"
						isLoading={isLoading}
						disabled={!isConfirmed}
						onClick={remove}
						data-test-id="delete-confirm-button"
					>
						{confirmText}
					</PrimaryButton>
				</ModalFooter>
			</div>
		</Modal>
	);
};
